import { useCallback, useMemo, useState } from "react";
import { useSchemaDefinitions, useSchemasByService } from "@/hooks/useApi";
import type { SchemaEntrySummary } from "@/types/api";
import {
  type DeployProblem,
  type DeployProblemInput,
  deployProblems,
  type ProblemTarget,
} from "./deployProblems";
import { collectPipelineRefs } from "./pipelineLint";
import { usePipelineStaging } from "./pipelineStaging";
import {
  type OpenPipeline,
  type StudioPipelineValue,
  useStudioPipeline,
} from "./StudioPipelineProvider";
import { parseSchemaRowId, type StagedSchema } from "./stagingModel";
import { failureText } from "./studioPipelineJobs";

// Which name the header's modal is asking for.
export type NameModalKind = "save" | "deploy" | "duplicate";

// A rejected "Save" (draft state), tagged with the draft it belongs to.
export interface SaveFailure {
  draftId: string;
  message: string;
}

/** A schema ref as the gate resolves it: the service whose store holds
 *  the schema and the name or id the code uses there. */
interface CodeSchemaRef {
  serviceName: string;
  resource: string;
}

/** A store entry the gate fetches: its service and entry id. */
type SchemaEntryRef = { serviceName: string; entryId: string };

// The schemas the code's schemas block references, each on the service
// its entry names (schemas live per service, TTTL-319) — what a Full view
// deploy sends. Unparseable code yields none: the lint verdict already
// gates that.
function codeSchemaRefs(code: string | null): CodeSchemaRef[] {
  if (code === null) return [];
  let doc: unknown;
  try {
    doc = JSON.parse(code);
  } catch {
    return [];
  }
  return collectPipelineRefs(doc)
    .filter((r) => r.kind === "schema" && r.resource !== "")
    .map((r) => ({ serviceName: r.serviceName, resource: r.resource }));
}

const rowKey = (ref: SchemaEntryRef) =>
  `schema:${ref.serviceName}:${ref.entryId}`;

type DefinitionsByRow = Record<string, string | null>;

// The code's schema refs as store entries: undefined while that service's
// list loads, or when it has no entry of that id or name (the linter
// reports the dangling ref).
function resolveCodeEntries(
  refs: CodeSchemaRef[],
  listsByService: Record<string, { data?: SchemaEntrySummary[] }>,
): Array<SchemaEntryRef | undefined> {
  return refs.map((ref) => {
    const entry = listsByService[ref.serviceName]?.data?.find(
      (e) => e.id === ref.resource || e.name === ref.resource,
    );
    return entry
      ? { serviceName: ref.serviceName, entryId: entry.id }
      : undefined;
  });
}

// The staged schemas' stored texts keyed by staged id
// ("schema:<service>:<entry>"), present only once the entry's fetch
// answered.
function stagedSchemaDefinitions(
  schemas: StagedSchema[],
  byRow: DefinitionsByRow,
): DefinitionsByRow {
  const map: DefinitionsByRow = {};
  for (const s of schemas) {
    if (s.id in byRow) map[s.id] = byRow[s.id];
  }
  return map;
}

// The code's schema refs with their stored texts, as the gate reads
// them: undefined while the service's list or the entry still loads,
// null when the list doesn't know the ref (nothing stored to check).
function codeSchemaTexts(
  refs: CodeSchemaRef[],
  entries: Array<SchemaEntryRef | undefined>,
  listsByService: Record<string, { data?: SchemaEntrySummary[] }>,
  byRow: DefinitionsByRow,
): DeployProblemInput["codeSchemas"] {
  return refs.map((ref, i) => {
    const entry = entries[i];
    const serviceName = ref.serviceName;
    if (entry === undefined) {
      const listLoaded = listsByService[serviceName]?.data !== undefined;
      return {
        serviceName,
        name: ref.resource,
        text: listLoaded ? null : undefined,
      };
    }
    return { serviceName, name: ref.resource, text: byRow[rowKey(entry)] };
  });
}

// The stored schema texts the gate checks (TTTL-216): a saved schema
// deploys as stored, so every staged schema's text counts — and in the
// Full view so do the code draft's schema refs (names or ids), which may
// not be staged yet: a pipeline opened straight into the Full view
// stages nothing until the Stages switch. Both resolve against the
// stores of the services they name, then one fetch per entry.
function useStoredSchemaTexts(
  schemas: StagedSchema[],
  codeText: string | null,
): Pick<DeployProblemInput, "schemaDefinitions" | "codeSchemas"> {
  const stagedEntries = useMemo(
    () =>
      schemas.flatMap((s) => {
        const owner = parseSchemaRowId(s.id);
        return owner ? [owner] : [];
      }),
    [schemas],
  );
  const codeRefs = useMemo(() => codeSchemaRefs(codeText), [codeText]);
  const codeServices = useMemo(
    () => Array.from(new Set(codeRefs.map((r) => r.serviceName))),
    [codeRefs],
  );
  const { byService } = useSchemasByService(codeServices);
  const codeEntries = useMemo(
    () => resolveCodeEntries(codeRefs, byService),
    [codeRefs, byService],
  );
  const entryRefs = useMemo(() => {
    const seen = new Map<string, SchemaEntryRef>();
    for (const ref of stagedEntries) seen.set(rowKey(ref), ref);
    for (const ref of codeEntries) if (ref) seen.set(rowKey(ref), ref);
    return Array.from(seen.values());
  }, [stagedEntries, codeEntries]);
  const byRow = useSchemaDefinitions(entryRefs);
  const schemaDefinitions = useMemo(
    () => stagedSchemaDefinitions(schemas, byRow),
    [schemas, byRow],
  );
  const codeSchemas = useMemo(
    () => codeSchemaTexts(codeRefs, codeEntries, byService, byRow),
    [codeRefs, codeEntries, byService, byRow],
  );
  return { schemaDefinitions, codeSchemas };
}

// What a Deploy press would have to get past: the editors' errors and the
// Full view's lint verdict (deployProblems). Deploy itself stays pressable;
// the header shows this list instead of deploying while it isn't empty.
function useDeployProblems(): DeployProblem[] {
  const {
    studioView,
    pipelineCodeDraft,
    pipelineCodeLintStatus,
    pipelineCodeErrors,
    inputDocument,
    schemaDrafts,
    scriptDrafts,
    sources,
    transforms,
    sinks,
    schemas,
    seedingStageIds,
    pipelineExtras,
  } = usePipelineStaging();
  const { schemaDefinitions, codeSchemas } = useStoredSchemaTexts(
    schemas,
    studioView === "full" ? (pipelineCodeDraft?.text ?? null) : null,
  );
  return useMemo(
    () =>
      deployProblems({
        studioView,
        lintStatus: pipelineCodeLintStatus,
        codeErrorCount: pipelineCodeErrors.length,
        inputDocument,
        schemaDrafts,
        scriptDrafts,
        schemas,
        schemaDefinitions,
        codeSchemas,
        seedingStageIds,
        stages: { sources, transforms, sinks },
        inputEncodings: pipelineExtras.inputEncodings,
      }),
    [
      pipelineExtras.inputEncodings,
      studioView,
      pipelineCodeLintStatus,
      pipelineCodeErrors,
      inputDocument,
      schemaDrafts,
      scriptDrafts,
      schemas,
      schemaDefinitions,
      codeSchemas,
      seedingStageIds,
      sources,
      transforms,
      sinks,
    ],
  );
}

// The staging calls a problem row's navigation needs.
interface GoToDeps {
  staging: ReturnType<typeof usePipelineStaging>;
  prepareStagesSwitch: StudioPipelineValue["prepareStagesSwitch"];
}

// The Stages view; false when the Full → Stages hand-over refuses (code
// with errors can't become stages) — the code row is in the same list.
async function ensureStagesView(deps: GoToDeps): Promise<boolean> {
  if (deps.staging.studioView !== "full") return true;
  const result = await deps.prepareStagesSwitch();
  return result.kind === "switched";
}

function selectStageRow(
  target: Extract<ProblemTarget, { kind: "schema" | "script" }>,
  staging: GoToDeps["staging"],
): void {
  staging.setStageTab(target.tab);
  if (staging.effectiveSelectedId !== target.rowId) {
    staging.toggleSelected(target.rowId);
  }
}

async function goToProblem(
  { target }: DeployProblem,
  deps: GoToDeps,
): Promise<void> {
  if (target.kind === "code") {
    deps.staging.setStudioView("full");
    return;
  }
  if (!(await ensureStagesView(deps))) return;
  if (target.kind === "input") {
    deps.staging.setStageTab("source");
    return;
  }
  selectStageRow(target, deps.staging);
}

// Takes the user to the tab or view a problem row names — no cursor
// placement; the editor's own error button does that.
export function useGoToProblem(): (problem: DeployProblem) => void {
  const staging = usePipelineStaging();
  const { prepareStagesSwitch } = useStudioPipeline();
  return useCallback(
    (problem: DeployProblem) => {
      void goToProblem(problem, { staging, prepareStagesSwitch });
    },
    [staging, prepareStagesSwitch],
  );
}

// Deploying an empty studio would create an empty job; a deploy already
// in flight must not be fired twice (it creates a job).
function deployIsDisabled(pipelineDirty: boolean, deploying: boolean): boolean {
  return !pipelineDirty || deploying;
}

// A stopping job still shows the Stop button (disabled by jobControl)
// rather than flipping to Start before it settles.
function jobIsRunning(jobControl: StudioPipelineValue["jobControl"]): boolean {
  return (
    jobControl?.displayState === "Running" ||
    jobControl?.displayState === "Stopping"
  );
}

const draftIdOf = (openPipeline: OpenPipeline | null): string | null =>
  openPipeline?.kind === "draft" ? openPipeline.workspace.id : null;

export interface PipelineActionState {
  openPipeline: OpenPipeline | null;
  pipelineDirty: boolean;
  problems: DeployProblem[];
  deployDisabled: boolean;
  running: boolean;
  openDraftId: string | null;
  isDeployed: boolean;
}

// Everything the header's lifecycle buttons read off the open pipeline —
// which of them show, which are pressable and why not.
export function usePipelineActionState(): PipelineActionState {
  const { pipelineDirty } = usePipelineStaging();
  const { openPipeline, jobControl, deploying } = useStudioPipeline();
  const problems = useDeployProblems();
  return {
    openPipeline,
    pipelineDirty,
    problems,
    deployDisabled: deployIsDisabled(pipelineDirty, deploying),
    running: jobIsRunning(jobControl),
    openDraftId: draftIdOf(openPipeline),
    isDeployed: openPipeline?.kind === "deployed",
  };
}

// The lifecycle errors win the shared toast; a rejected Save only shows
// while the draft it failed on is the one still open.
export function resolveActionError(
  actionError: string | null,
  saveError: SaveFailure | null,
  openDraftId: string | null,
): string | null {
  if (saveError === null || saveError.draftId !== openDraftId) {
    return actionError;
  }
  return actionError ?? saveError.message;
}

/** A save held back because the draft's stopped job started running in
 *  the meantime — retiring it needs a stop, which is the user's call. */
export interface SaveHold {
  draftId: string;
  jobName: string;
}

// The draft state's Save: the provider's saveOpenDraft with its outcomes
// turned into header state — the failure toast (SaveFailure, tagged with
// the draft) and the "stop the job and save?" question (SaveHold), which
// confirmStop answers by saving again with the stop allowed.
export function useDraftSave(
  saveOpenDraft: StudioPipelineValue["saveOpenDraft"],
) {
  const [saveError, setSaveError] = useState<SaveFailure | null>(null);
  const [hold, setHold] = useState<SaveHold | null>(null);
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (draftId: string, stopJob: boolean) => {
      setSaveError(null);
      setSaving(true);
      try {
        const outcome = await saveOpenDraft({ stopJob });
        setHold(
          outcome.kind === "job-running"
            ? { draftId, jobName: outcome.jobName }
            : null,
        );
      } catch (e) {
        setHold(null);
        setSaveError({ draftId, message: `Save failed: ${failureText(e)}` });
      } finally {
        setSaving(false);
      }
    },
    [saveOpenDraft],
  );

  return {
    saveError,
    dismissSaveError: () => setSaveError(null),
    hold,
    saving,
    saveDraft: (draftId: string) => save(draftId, false),
    confirmStop: () => hold && save(hold.draftId, true),
    cancelStop: () => setHold(null),
  };
}
